/**
 * Configure an evaluator: the definition on the left, a sample document and the
 * Test button on the right. Creating saves v1; editing saves only what changed,
 * as version n+1 — scores already made keep the version they used. An
 * archived one is read-only, with a banner to restore it.
 */

import { useState } from "react";
import { useNavigate } from "react-router";
import { Button } from "@/components/ui/button";
import { Crumbs, PageHeader } from "@/components/ui/page";
import { Pill } from "@/components/ui/pill";
import { errorText } from "@/components/ui/states";
import { Tabs } from "@/components/ui/tabs";
import { ROLE_CANT_CHANGE } from "@/components/use-holds";
import { RunsTab } from "@/features/evaluators/runs/runs-tab";
import { ArchiveMenu } from "@/features/archive/archive-menu";
import { ArchivedBadge, ArchivedBanner } from "@/features/archive/archived";
import { useRestore } from "@/features/archive/hooks";
import { isArchived } from "@/features/archive/kinds";
import { plural } from "@/lib/format";
import { useProject } from "@/features/projects/hooks";
import { ProjectAction } from "@/features/projects/project-action";
import { useUrlState } from "@/lib/use-url-state";
import { useUiStore } from "@/stores/ui-store";
import { DefinitionPane } from "./definition-pane";
import { EvaluatorVersions } from "./evaluator-versions";
import { useCreateEvaluator, useEvaluatorPaths, useEvaluatorSample, usePatchEvaluator } from "./hooks";
import { KIND } from "./labels";
import { SamplePane } from "./sample-pane";
import { mappingView } from "./sample-paths";
import type { CodeCheck, Evaluator } from "./types";
import { useEvaluatorDraft, type DraftSeed } from "./use-evaluator-draft";
import { useSampleChoice } from "./use-sample-choice";

const TABS = ["definition", "experiments"] as const;
const TAB_LABEL = { definition: "Definition", experiments: "Experiments" };

export function ConfigureScreen({
  seed,
  checks,
  saved,
}: {
  seed: DraftSeed;
  checks: CodeCheck[];
  saved?: Evaluator | undefined;
}) {
  const d = useEvaluatorDraft(seed, checks, saved);
  const s = useSampleChoice();
  const [focused, setFocused] = useState<string | null>(null);
  const mayEdit = useProject().can("evaluators:write");
  const archived = isArchived(saved);
  const mayWrite = mayEdit && !archived;
  const restore = useRestore("evaluator");
  const usedBy = saved?.used_by?.experiments ?? 0;
  const paths = useEvaluatorPaths(s.pathsFor, !!s.pathsFor.dataset_id).data ?? [];
  const doc = useEvaluatorSample(s.ref).data;
  // An observation is shown raw; the mapping rows read it by mapping paths.
  const sample = doc && s.source === "trace" ? mappingView(doc) : doc;
  const create = useCreateEvaluator();
  const patch = usePatchEvaluator(saved?.id ?? "");
  const navigate = useNavigate();
  const url = useUrlState();
  // Only a path in this app: never an outside address.
  const returnTo = url.get("returnTo")?.startsWith("/") ? url.get("returnTo") : null;
  // A new evaluator has nothing to run yet, so it only has its definition.
  const tab = (saved && TABS.find((t) => t === url.get("tab"))) || "definition";
  const notify = useUiStore((st) => st.notify);
  const next = (saved?.version ?? 0) + 1;
  const unchanged = !!saved && Object.keys(d.patch ?? {}).length === 0;
  const busy = create.isPending || patch.isPending;

  const pick = (path: string) => {
    const target = focused ?? d.rows[0]?.name;
    if (target) (d.setPath(target, path), setFocused(target));
  };
  const failed = (e: unknown) => notify(errorText(e, "Could not save the evaluator."), "error");
  const save = () =>
    saved
      ? patch.mutate(d.patch ?? {}, {
          onSuccess: (n) => {
            notify(`Saved as v${n.version}. Earlier scores keep their version.`);
            navigate(`/evaluators/${n.id}`, { replace: true });
          },
          onError: failed,
        })
      : create.mutate(d.body, {
          onSuccess: (made) => {
            notify(`Created ${made.name}.`);
            navigate(returnTo ? withParam(returnTo, "evaluator", made.id) : "/evaluators");
          },
          onError: failed,
        });

  const title = saved?.name ?? (d.draft.kind === "llm_judge" ? "New LLM judge" : "New code check");
  return (
    <div className="flex flex-col gap-4">
      <Crumbs items={[{ label: "Evaluators", to: "/evaluators" }, { label: saved ? saved.name : "New" }]} />
      <PageHeader
        title={title}
        badges={
          <>
            <Pill tone={KIND[d.draft.kind].tone}>{KIND[d.draft.kind].label}</Pill>
            {saved && <span className="font-mono text-xs text-muted">v{saved.version}</span>}
            {archived && <ArchivedBadge />}
          </>
        }
        subtitle="Every score is between 0 and 1 and comes with a written reason."
        actions={saved && !archived && <ArchiveMenu kind="evaluator" row={saved} size="md" />}
      />
      {saved && (
        <ArchivedBanner
          kind="evaluator"
          row={saved}
          mayRestore={mayEdit}
          onRestore={() => void restore(saved)}
        >
          {usedBy > 0 && `Still used by ${plural(usedBy, "past experiment")}.`}
        </ArchivedBanner>
      )}
      {saved && (
        <Tabs
          label="Evaluator sections"
          value={tab}
          onChange={(t) => url.set({ tab: t === "definition" ? null : t })}
          items={TABS.map((key) => ({ key, label: TAB_LABEL[key] }))}
        />
      )}
      {tab === "experiments" && saved && <RunsTab evaluatorKey={saved.key} evaluatorName={saved.name} />}
      {tab === "definition" && (
        <>
          <div className="grid items-start gap-4 lg:grid-cols-2">
            <div className="flex min-w-0 flex-col gap-4">
              <DefinitionPane
                d={d}
                checks={checks}
                model={saved?.model ?? null}
                paths={paths}
                sample={sample}
                focused={focused}
                onFocus={setFocused}
              />
              {saved && saved.version > 1 && <EvaluatorVersions evaluator={saved} />}
            </div>
            {/* Pinned while the definition scrolls, so a mapping row and the sample stay side by side. */}
            <div className="min-w-0 lg:sticky lg:top-4 lg:max-h-[calc(100vh-7rem)] lg:overflow-y-auto">
              <SamplePane s={s} body={d.testBody} problem={d.testProblem} focused={focused} onPick={pick} />
            </div>
          </div>
          <div className="sticky bottom-0 z-30 -mx-8 -mb-7 flex items-center justify-end gap-3 border-t border-line-soft bg-well px-8 py-3">
            {archived && <span className="text-xs text-muted">Restore it to change it.</span>}
            {!mayEdit && !archived && <span className="text-xs text-muted">{ROLE_CANT_CHANGE}</span>}
            {mayWrite && d.problem && <span className="text-xs text-muted">{d.problem}</span>}
            {mayWrite && saved && !d.problem && (
              <span className="text-xs text-muted">
                {unchanged ? "No changes yet." : `Experiments scored with v${saved.version} keep it.`}
              </span>
            )}
            <Button onClick={() => navigate(returnTo ?? "/evaluators")}>Cancel</Button>
            {!archived && (
              <ProjectAction permission="evaluators:write">
                <Button variant="primary" disabled={!!d.problem || unchanged || busy} onClick={save}>
                  {busy ? "Saving…" : saved ? `Save as v${next}` : "Create"}
                </Button>
              </ProjectAction>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/** `path` with `key=value` added to its query. */
function withParam(path: string, key: string, value: string): string {
  const url = new URL(path, "http://app");
  url.searchParams.set(key, value);
  return url.pathname + url.search;
}
